import FormValidation, { useFormValidation } from "common/ui/FormValidation";
import ModalWrapper from "common/ui/Modal";
import NumberEditor from "common/ui/NumberEditor";
import TypedSelect from "common/ui/TypedSelect";
import Contract from "domain/contract";
import useUnitHelper from "features/settings/commodities/useUnitHelper";
import React, { useMemo, useState, useEffect } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import contractService from "./contractService";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import unitHelper from "domain/unitHelper";
import useUiCustomization from "features/merchants/useUiCustomization";
import { FormField } from "domain/formFieldSetting";
import useEditableContractFields from "features/merchants/useEditableContractFields";
import { localizeOfferContract } from "features/localization/localizeService";
import loggingService from "common/logging/loggingService";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { ErpSystem } from "domain/erpSystem";
import contractTypeHelper from "domain/contractTypeHelper";

interface ChangeContractQuantityModalProps {
    contract: Contract;
    isSupervisorEditing: boolean;
    onClose(): void;
    onContractUpdated(contract: Contract): void;
}

enum ChangeAction {
    Add,
    Remove,
    Undo,
    Cancel
}


const ChangeContractQuantityModal: React.FC<ChangeContractQuantityModalProps> = React.memo(props => {

    const [changeAction, setChangeAction] = useState<ChangeAction>(null);
    const [changeInQuantity, setChangeInQuantity] = useState(0);
    const [comments, setComments] = useState(null);
    const [parentContract, setParentContract] = useState<Contract | null>(null);

    const formValidator = useFormValidation();

    const contractUnitHelper = useUnitHelper().forContract(props.contract);
    const unitConversionRates = useUnitConversionRates();
    const uiCustomization = useUiCustomization();
    const editableFields = useEditableContractFields();
    const activeMerchant = useActiveMerchant();
    const quantityPrecision = uiCustomization.addEditOfferContractQuantityDecimalPrecision;

    const showLocalUOM = props.contract?.commodity?.baseUnit.id !== props.contract?.commodity?.localUnit.id;

    const isAdding = changeAction === ChangeAction.Add;

    const cancellableQuantity = Math.max(0, contractTypeHelper.isCash(props.contract.contractType)
        ? props.contract.quantityContracted
        : props.contract.remainingQuantity);

    // Fetch parent contract if this is a child
    useEffect(() => {
        async function fetchParent() {
            if (props.contract.sourceContractId != null) {
                try {
                    const parent = await contractService.getContract(props.contract.sourceContractId);
                    setParentContract(parent);
                } catch (error) {
                    loggingService.error("Failed to fetch parent contract:", error as Error);
                }
            }
        }
        fetchParent();
    }, [props.contract.sourceContractId]);

    async function onSave() {
        if (!formValidator.current.validate()) return;

        const updatedContract = await contractService.updateQuantity(
            props.contract.id,
            isAdding ? changeInQuantity : -changeInQuantity,
            changeAction === ChangeAction.Undo,
            props.isSupervisorEditing,
            comments,
            changeAction === ChangeAction.Cancel);
        props.onContractUpdated?.(updatedContract);
        props.onClose();
    }

    const actionOptions = useMemo(() => {
        const opts = [ChangeAction.Add, ChangeAction.Remove];
        if (props.contract.sourceContractId != null) {
            opts.push(ChangeAction.Undo);
        }
        if (activeMerchant?.erpSystem === ErpSystem.AgrisDataHub) {
            opts.push(ChangeAction.Cancel);
        }
        return opts;
    }, [props.contract, activeMerchant])

    function getChangeInQuantityLabel(): string {
        if (changeAction === ChangeAction.Cancel) return "Quantity to Cancel";
        return isAdding ? "Additional Quantity" : "Quantity to Remove";
    }

    function getMaxQuantity(): number | null {
        if (isAdding) return null;

        return changeAction === ChangeAction.Cancel ? cancellableQuantity : props.contract.quantityContracted;
    }

    function getNewQuantity(): number {
        if (isAdding) return props.contract.quantityContracted + changeInQuantity;

        return props.contract.quantityContracted - changeInQuantity;
    }

    function getAlertText(): string {
        const quantityText = unitHelper.getQuantityTextWithUnits(changeInQuantity, props.contract.commodity, unitConversionRates, quantityPrecision);
        switch (changeAction) {
            case ChangeAction.Add:
                return `${quantityText} will be added to this contract`;
            case ChangeAction.Remove:
                return `${quantityText} will be removed from this contract`;
            case ChangeAction.Undo:
                return `${quantityText} will be added back to the parent contract`;
            case ChangeAction.Cancel:
                return `${quantityText} will be cancelled from the contract`;
            default:
                return '';
        }
    }

    return <ModalWrapper title="Change Contract Quantity"
        async
        onAdvance={onSave}
        onCancel={props.onClose}
        advanceButtonText="Save"
        isAdvanceButtonDisabled={changeAction == null || changeInQuantity === 0}>
        {props.isSupervisorEditing && <Alert variant="warning">{localizeOfferContract("EditContractModal_Alert_SupervisorEdit")}</Alert>}

        <FormValidation ref={formValidator}>
            <Form.Group as={Row}>
                <Form.Label column>Current Quantity</Form.Label>
                <Col>
                    {contractUnitHelper.getQuantityText(props.contract.quantityContracted, quantityPrecision)}
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>Action</Form.Label>
                <Col>
                    <TypedSelect required
                        options={actionOptions}
                        showNullRow
                        nullRowText="--Select--"
                        value={changeAction}
                        valueGetter={m => m}
                        textGetter={getChangeActionText}
                        onValueSelected={m => {
                            setChangeAction(m);
                            if (m === ChangeAction.Undo) {
                                setChangeInQuantity(props.contract.quantityContracted);
                            } else if (m === ChangeAction.Cancel) {
                                setChangeInQuantity(cancellableQuantity);
                            }
                        }} />
                </Col>
            </Form.Group>

            {changeAction != null && <>
                <Form.Group as={Row}>
                    <Form.Label column>{getChangeInQuantityLabel()}</Form.Label>
                    {!showLocalUOM && <Col>
                        <NumberEditor value={changeInQuantity}
                            min={0}
                            max={getMaxQuantity()}
                            disabled={changeAction === ChangeAction.Undo}
                            step={0.01}
                            allowDecimals
                            enforcePrecision
                            precision={quantityPrecision}
                            thousandSeparator
                            unit={props.contract.commodity.baseUnit.abbreviation}
                            onValueChange={m => setChangeInQuantity(m)}
                        />
                    </Col>}
                    {showLocalUOM && <>
                        <Col xs="4">
                            <NumberEditor value={changeInQuantity}
                                min={0}
                                max={getMaxQuantity()}
                                step={0.01}
                                allowDecimals
                                precision={quantityPrecision}
                                thousandSeparator
                                unit={props.contract.commodity.baseUnit.abbreviation}
                                onValueChange={m => setChangeInQuantity(m)}
                            />
                        </Col>
                        <Col xs="4" className="pl-0">
                            <NumberEditor
                                min={0}
                                max={isAdding ? null : unitHelper.getLocalQuantity(getMaxQuantity(), props.contract.commodity, unitConversionRates, quantityPrecision)}
                                step={0.01}
                                allowDecimals
                                precision={quantityPrecision}
                                thousandSeparator
                                unit={props.contract.commodity.localUnit.abbreviation}
                                value={unitHelper.getLocalQuantity(changeInQuantity, props.contract.commodity, unitConversionRates, quantityPrecision) ?? 0}
                                onValueChange={value => {
                                    const quantityInBase = unitHelper.getBaseQuantity(value, props.contract.commodity, unitConversionRates, quantityPrecision);
                                    if (quantityInBase != null) {
                                        setChangeInQuantity(quantityInBase);
                                    }
                                }}
                            />
                        </Col>
                    </>}
                </Form.Group>

                {changeAction === ChangeAction.Undo ? null : (
                    <Form.Group as={Row}>
                        <Form.Label column>New Quantity</Form.Label>
                        <Col>
                            {unitHelper.getQuantityTextWithUnits(getNewQuantity(), props.contract.commodity, unitConversionRates, quantityPrecision)}
                        </Col>
                    </Form.Group>
                )}

                <Form.Group as={Row}>
                    <Form.Label column className="optional">Comment</Form.Label>
                    <Col>
                        <Form.Control
                            value={comments}
                            disabled={!editableFields.data.includes(FormField.Comment)}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setComments(ev.target.value)}>
                        </Form.Control>
                    </Col>
                </Form.Group>
            </>}

            {getAlertText() === '' ? null : <Alert variant="info">{getAlertText()}</Alert>}

            {changeAction === ChangeAction.Add && parentContract != null && changeInQuantity > 0 &&
                (() => {
                    const deficit = changeInQuantity - parentContract.remainingQuantity;
                    if (deficit > 0) {
                        return <Alert variant="info">
                            Parent contract quantity will be increased by {unitHelper.getQuantityTextWithUnits(deficit, props.contract.commodity, unitConversionRates, quantityPrecision)}. Total unpriced quantity will not change.
                        </Alert>;
                    }
                    return null;
                })()
            }

        </FormValidation>
    </ModalWrapper>;
});


function getChangeActionText(action: ChangeAction) {
    switch (action) {
        case ChangeAction.Add:
            return "Add Units";
        case ChangeAction.Remove:
            return "Subtract Units";
        case ChangeAction.Undo:
            return "Undo Units";
        case ChangeAction.Cancel:
            return "Cancel Units";
        default:
            return "Unknown option";
    }
}

export default ChangeContractQuantityModal;